import { type VirtualItem, type Virtualizer } from '@tanstack/react-virtual';
import { useCallback, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { type TranscriptSegmentData } from '~/components/AssetModal/shared/components/TranscriptPanel/types';

type UsePersistCurrentVisibleSegmentOnLoadParams<
  TScrollElement extends Element | Window,
  TItemElement extends Element,
> = {
  virtualizer: Virtualizer<TScrollElement, TItemElement>;
  segments: TranscriptSegmentData[];
};

/**
 * This hook is used to persist the current scroll position of the transcript segment list.
 * Virtualized list persists the scroll position by default when new items are loaded on the bottom of the list.
 * But with segments, new items may be loaded on top of the current element. Scroll will be persisted in this case,
 * but the current element will be scrolled out of the view. This hook is used to scroll the list to the previous visible element
 */
export const usePersistItemPositionOnLoad = <T extends Element | Window, K extends Element>({
  virtualizer,
  segments,
}: UsePersistCurrentVisibleSegmentOnLoadParams<T, K>) => {
  const virtualizerRef = useRef<Virtualizer<T, K>>();

  const previousSegmentsLengthRef = useRef(0);
  const measurementsCacheSnapshot = useRef<typeof virtualizer.measurementsCache>([]);

  const totalSize = virtualizer.getTotalSize();

  const previousVisibleItemRef = useRef<VirtualItem<K>>();

  useIsomorphicLayoutEffect(() => {
    virtualizerRef.current = virtualizer;
  });

  useIsomorphicLayoutEffect(() => {
    if (virtualizerRef.current && !!segments.length && previousSegmentsLengthRef.current !== segments.length) {
      if (previousVisibleItemRef.current) {
        // find new element that is on position of previous visible element
        const currentItemAtPreviousPosition =
          virtualizerRef.current.measurementsCache[previousVisibleItemRef.current.index];

        const previousKey = previousVisibleItemRef.current.key;
        const currentKey = currentItemAtPreviousPosition.key;

        // If elements were loaded on top of current segment, previous and current element at given index will be different.
        // We need to find offset between new and old element and scroll the list with that offset to persist previous position
        if (previousKey !== currentKey) {
          const scrolledElement = virtualizerRef.current.measurementsCache.find(
            (measurement) => measurement.key === previousKey,
          );
          if (scrolledElement) {
            const elementOffset = scrolledElement.start - previousVisibleItemRef.current.start;
            virtualizerRef.current.scrollBy(elementOffset);
          }
        }
      }

      measurementsCacheSnapshot.current = [];
    }

    return () => {
      if (!virtualizerRef.current) return;

      previousSegmentsLengthRef.current = segments.length;
      const scrollOffset = virtualizerRef.current.scrollOffset ?? 0;
      // find first visible fetched segments item in current offset before new elements are loaded
      previousVisibleItemRef.current = measurementsCacheSnapshot.current?.find((measurement, index) => {
        const isBelowOffset = measurement.start >= scrollOffset;
        const isOffsetBetweenItem = measurement.start <= scrollOffset && scrollOffset <= measurement.end;
        const isOnScreen = isBelowOffset || isOffsetBetweenItem;
        return isOnScreen && segments[index].type === 'fetched';
      });
    };
  }, [totalSize, segments]);

  const createPositionsSnapshot = useCallback(() => {
    measurementsCacheSnapshot.current = virtualizerRef.current ? [...virtualizerRef.current.measurementsCache] : [];
  }, []);

  return { createPositionsSnapshot };
};
